/**
 * JoinedCommunitiesWidget - Display communities the user has joined
 * Reusable component for both desktop and mobile layouts
 */

import React, { useState, useEffect } from 'react';
import { Globe, Users } from 'lucide-react';
import type { ThemeTokens } from '../../../shared/theme/tokens';
import { getCommunities } from '../../../lib/api-client';

interface JoinedCommunitiesWidgetProps {
  theme: ThemeTokens;
  className?: string;
  style?: React.CSSProperties;
  maxDisplay?: number;
  showHeader?: boolean;
  compact?: boolean;
  onNavigate?: (path: string) => void;
}

interface WidgetCommunity {
  id: string;
  name: string;
  nameSlug?: string;
  slug?: string;
  avatar?: string;
  memberCount: number;
  isMember: boolean;
}

export const JoinedCommunitiesWidget: React.FC<JoinedCommunitiesWidgetProps> = ({
  theme,
  className = '',
  style = {},
  maxDisplay = 10,
  showHeader = true,
  compact = false,
  onNavigate,
}) => {
  const navigate = onNavigate || ((path: string) => { window.location.href = path; });
  const [communities, setCommunities] = useState<WidgetCommunity[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const all = await getCommunities() as any[];
        setCommunities(all.filter((c: any) => c.isMember));
      } catch {
        setCommunities([]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <div
      className={`rounded-lg overflow-hidden ${className}`}
      style={{
        backgroundColor: theme.cardBg || 'rgba(255, 255, 255, 0.05)',
        borderColor: `${theme.accentColor}40`,
        border: '1px solid',
        ...style,
      }}
    >
      {showHeader && (
        <h3
          className={`font-bold text-center border-b ${compact ? 'py-2' : 'py-3'}`}
          style={{
            color: theme.textColor,
            borderColor: `${theme.accentColor}40`,
          }}
        >
          <Globe className="inline-block w-4 h-4 mr-2" />
          Joined
        </h3>
      )}

      <div className={compact ? 'p-2' : 'p-4'}>
        {isLoading ? (
          <div className="text-center py-4" style={{ color: theme.textColor + '99' }}>
            Loading...
          </div>
        ) : communities.length > 0 ? (
          <div className="space-y-3">
            {communities.slice(0, maxDisplay).map((c) => (
              <div
                key={c.id}
                onClick={() => navigate(`/community/${c.nameSlug || c.slug || c.id}`)}
                className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:opacity-80 transition-all"
                style={{ backgroundColor: theme.accentColor + '10' }}
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    background: c.avatar ? `url(${c.avatar}) center/cover` : theme.accentColor + '40',
                  }}
                >
                  {!c.avatar && <Globe size={18} style={{ color: theme.textColor }} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate text-sm" style={{ color: theme.textColor }}>
                    {c.name}
                  </p>
                  <p className="text-xs truncate flex items-center gap-1" style={{ color: theme.textColor + '99' }}>
                    <Users size={11} /> {c.memberCount} members
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-center py-6">
            <Globe size={48} style={{ color: theme.textColor + '40', marginBottom: '12px' }} />
            <p style={{ color: theme.textColor + '99', fontSize: '14px', marginBottom: '8px' }}>
              No joined communities yet
            </p>
            <p style={{ color: theme.textColor + '66', fontSize: '12px' }}>
              Explore and join communities
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
